import { useState, useEffect } from 'react';
import axios from 'axios';
import { Edit3, Save, X, FileText, Home, Info, Users, Phone } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { useToast } from '../../hooks/use-toast';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const pageIcons = {
  home: Home,
  about: Info,
  membership: Users,
  contact: Phone
};

export default function AdminCMSPages() {
  const [pages, setPages] = useState([]);
  const [selectedPage, setSelectedPage] = useState(null);
  const [editContent, setEditContent] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const { toast } = useToast();

  useEffect(() => {
    fetchPages();
  }, []);

  const fetchPages = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/cms/pages`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setPages(response.data);
    } catch (error) {
      console.error('Error fetching pages:', error);
      toast({ title: 'Error', description: 'Failed to load pages', variant: 'destructive' });
    } finally {
      setLoading(false);
    }
  };

  const handleSelectPage = async (pageName) => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/cms/pages/${pageName}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSelectedPage(pageName);
      setEditContent(JSON.stringify(response.data.content, null, 2));
    } catch (error) {
      console.error('Error fetching page:', error);
      toast({ title: 'Error', description: 'Failed to load page content', variant: 'destructive' });
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const token = localStorage.getItem('token');
      const content = JSON.parse(editContent);
      await axios.put(`${API}/cms/pages/${selectedPage}`, { content }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast({ title: 'Success', description: 'Page content updated successfully' });
      fetchPages();
    } catch (error) {
      console.error('Error saving page:', error);
      toast({ title: 'Error', description: error.message || 'Failed to save page content', variant: 'destructive' });
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#0E285B]"></div>
      </div>
    );
  }

  return (
    <div data-testid="cms-pages-container">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="font-['Playfair_Display'] text-2xl font-bold text-[#0E285B]">Page Content Management</h1>
          <p className="text-slate-500 text-sm mt-1">Edit content for all website pages</p>
        </div>
      </div>

      <div className="grid lg:grid-cols-4 gap-6">
        {/* Page List */}
        <div className="lg:col-span-1">
          <div className="bg-white rounded-sm shadow-sm border border-slate-200 p-4">
            <h2 className="font-semibold text-[#0E285B] mb-4">Pages</h2>
            <div className="space-y-2">
              {['home', 'about', 'membership', 'contact'].map((pageName) => {
                const Icon = pageIcons[pageName] || FileText;
                return (
                  <button
                    key={pageName}
                    onClick={() => handleSelectPage(pageName)}
                    className={`w-full flex items-center gap-3 px-4 py-3 rounded-sm text-left transition-colors ${
                      selectedPage === pageName
                        ? 'bg-[#0E285B] text-white'
                        : 'bg-slate-50 text-slate-700 hover:bg-slate-100'
                    }`}
                    data-testid={`cms-page-${pageName}`}
                  >
                    <Icon className="h-4 w-4" />
                    <span className="capitalize font-medium">{pageName}</span>
                  </button>
                );
              })}
            </div>
          </div>
        </div>

        {/* Editor */}
        <div className="lg:col-span-3">
          {selectedPage ? (
            <div className="bg-white rounded-sm shadow-sm border border-slate-200">
              <div className="flex items-center justify-between p-4 border-b border-slate-200">
                <h2 className="font-semibold text-[#0E285B] capitalize flex items-center gap-2">
                  <Edit3 className="h-4 w-4" />
                  Editing: {selectedPage}
                </h2>
                <div className="flex gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setSelectedPage(null)}
                    data-testid="cms-cancel-btn"
                  >
                    <X className="h-4 w-4 mr-1" />
                    Cancel
                  </Button>
                  <Button
                    size="sm"
                    onClick={handleSave}
                    disabled={saving}
                    className="bg-[#0E285B] hover:bg-[#0E285B]/90"
                    data-testid="cms-save-btn"
                  >
                    <Save className="h-4 w-4 mr-1" />
                    {saving ? 'Saving...' : 'Save Changes'}
                  </Button>
                </div>
              </div>
              <div className="p-4">
                <p className="text-sm text-slate-500 mb-3">
                  Edit the JSON content below. Make sure to maintain valid JSON format.
                </p>
                <textarea
                  value={editContent || ''}
                  onChange={(e) => setEditContent(e.target.value)}
                  className="w-full h-[500px] font-mono text-sm p-4 border border-slate-200 rounded-sm focus:outline-none focus:ring-2 focus:ring-[#0E285B]"
                  data-testid="cms-content-editor"
                />
              </div>
            </div>
          ) : (
            <div className="bg-white rounded-sm shadow-sm border border-slate-200 p-12 text-center">
              <FileText className="h-12 w-12 text-slate-300 mx-auto mb-4" />
              <h3 className="text-lg font-medium text-slate-600 mb-2">Select a Page</h3>
              <p className="text-slate-400">Choose a page from the list to edit its content</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
